import React, { useMemo } from 'react';
import { cn } from 'src/tef/components/utils/cn';
import { forwardProps } from 'src/tef/components/utils/forwardProps';
import type { BaseComponentState, ComponentSize, ForwardedHTMLProps } from 'src/tef/components/utils/types';

type FlexDirection = 'row' | 'col' | 'row-reverse' | 'col-reverse';
type FlexAlign = 'start' | 'end' | 'center' | 'stretch' | 'baseline';
type FlexJustify = 'start' | 'end' | 'center' | 'between' | 'around' | 'evenly';
type GapSize = ComponentSize | '2xl' | 'none';
type ContainerElement =
  | 'div'
  | 'section'
  | 'article'
  | 'aside'
  | 'nav'
  | 'header'
  | 'footer'
  | 'main'
  | 'ul'
  | 'ol'
  | 'form'
  | 'fieldset';

interface FlexContainerProps extends React.HTMLAttributes<HTMLElement>, ForwardedHTMLProps, BaseComponentState {
  children: React.ReactNode;
  className?: string;
  as?: ContainerElement;
  direction?: FlexDirection;
  align?: FlexAlign;
  justify?: FlexJustify;
  wrap?: boolean | 'reverse';
  gap?: GapSize;
  gapX?: GapSize;
  gapY?: GapSize;
  inline?: boolean;
  grow?: boolean;
  shrink?: boolean;
  basis?: 'auto' | 'full' | '0';
  fullWidth?: boolean;
  fullHeight?: boolean;
}

const gapSizes: Record<GapSize, string> = {
  none: 'gap-0',
  xs: 'gap-1',
  sm: 'gap-2',
  md: 'gap-4',
  lg: 'gap-6',
  xl: 'gap-8',
  '2xl': 'gap-10',
};

const gapXSizes: Record<GapSize, string> = {
  none: 'gap-x-0',
  xs: 'gap-x-1',
  sm: 'gap-x-2',
  md: 'gap-x-4',
  lg: 'gap-x-6',
  xl: 'gap-x-8',
  '2xl': 'gap-x-10',
};

const gapYSizes: Record<GapSize, string> = {
  none: 'gap-y-0',
  xs: 'gap-y-1',
  sm: 'gap-y-2',
  md: 'gap-y-4',
  lg: 'gap-y-6',
  xl: 'gap-y-8',
  '2xl': 'gap-y-10',
};

const directionMap: Record<FlexDirection, string> = {
  row: 'flex-row',
  col: 'flex-col',
  'row-reverse': 'flex-row-reverse',
  'col-reverse': 'flex-col-reverse',
};

const alignMap: Record<FlexAlign, string> = {
  start: 'items-start',
  end: 'items-end',
  center: 'items-center',
  stretch: 'items-stretch',
  baseline: 'items-baseline',
};

const justifyMap: Record<FlexJustify, string> = {
  start: 'justify-start',
  end: 'justify-end',
  center: 'justify-center',
  between: 'justify-between',
  around: 'justify-around',
  evenly: 'justify-evenly',
};

export const FlexContainer = React.memo(
  React.forwardRef<HTMLDivElement, FlexContainerProps>(
    (
      {
        children,
        className,
        as = 'div',
        direction = 'row',
        align = 'stretch',
        justify = 'start',
        wrap = false,
        gap,
        gapX,
        gapY,
        inline = false,
        grow = false,
        shrink = true,
        basis,
        fullWidth = false,
        fullHeight = false,
        loading = false,
        disabled = false,
        ...props
      },
      ref
    ) => {
      const forwardedProps = forwardProps(props);

      const Component = as as React.ElementType;

      const containerClasses = useMemo(() => {
        const wrapClass = wrap === true ? 'flex-wrap' : wrap === 'reverse' ? 'flex-wrap-reverse' : 'flex-nowrap';

        return cn(
          inline ? 'inline-flex' : 'flex',
          directionMap[direction],
          alignMap[align],
          justifyMap[justify],
          wrapClass,
          gap && gapSizes[gap],
          gapX && gapXSizes[gapX],
          gapY && gapYSizes[gapY],
          grow && 'flex-grow',
          !shrink && 'flex-shrink-0',
          basis === 'auto' && 'basis-auto',
          basis === 'full' && 'basis-full',
          basis === '0' && 'basis-0',
          fullWidth && 'w-full',
          fullHeight && 'h-full',
          loading && 'animate-pulse',
          disabled && 'opacity-50 pointer-events-none',
          className
        );
      }, [
        direction,
        align,
        justify,
        wrap,
        gap,
        gapX,
        gapY,
        inline,
        grow,
        shrink,
        basis,
        fullWidth,
        fullHeight,
        loading,
        disabled,
        className,
      ]);

      return (
        <Component
          ref={ref}
          className={containerClasses}
          aria-busy={loading || undefined}
          aria-disabled={disabled || undefined}
          {...forwardedProps}
          {...props}
        >
          {children}
        </Component>
      );
    }
  )
);

FlexContainer.displayName = 'FlexContainer';

export default FlexContainer;
